import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { api } from '@/lib/api';
import { useAuth } from '@/contexts/auth';
import { formatDistanceToNow } from 'date-fns';

export function DreamsPage() {
  const { user } = useAuth();

  const { data, isLoading } = useQuery({
    queryKey: ['dreams'],
    queryFn: () => api.dreams.list(),
    enabled: !!user,
  });

  if (!user) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-white mb-4">Sign in to view your dreams</h1>
          <Link to="/login" className="btn-primary">
            Login
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-display font-bold text-white">My Dreams</h1>
          <p className="text-dream-300 mt-1">
            {data ? `${data.total} dreams recorded` : 'Loading...'}
          </p>
        </div>
        <Link to="/dreams/new" className="btn-primary">
          Record Dream
        </Link>
      </div>

      {isLoading ? (
        <div className="grid gap-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="card animate-pulse">
              <div className="h-6 bg-dream-700 rounded w-1/3 mb-3" />
              <div className="h-4 bg-dream-700 rounded w-full mb-2" />
              <div className="h-4 bg-dream-700 rounded w-2/3" />
            </div>
          ))}
        </div>
      ) : data?.items.length === 0 ? (
        <div className="card text-center py-12">
          <div className="w-16 h-16 rounded-full bg-dream-800 flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-dream-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
            </svg>
          </div>
          <h3 className="text-xl font-semibold text-white mb-2">No dreams yet</h3>
          <p className="text-dream-300 mb-6">Start recording your dreams to build your personal dream journal.</p>
          <Link to="/dreams/new" className="btn-primary">
            Record Your First Dream
          </Link>
        </div>
      ) : (
        <div className="grid gap-4">
          {data?.items.map((dream) => (
            <DreamCard key={dream.id} dream={dream} />
          ))}
        </div>
      )}
    </div>
  );
}

function DreamCard({ dream }: { dream: { id: string; createdAt: string; rawContent: string; status: string; visibility: string; stressCode?: number } }) {
  const statusColors = {
    new: 'status-new',
    start: 'status-processing',
    run: 'status-processing',
    img: 'status-processing',
    complete: 'status-complete',
  };

  return (
    <Link to="/dreams/$dreamId" params={{ dreamId: dream.id }} className="dream-card block">
      <div className="flex items-start justify-between gap-4 mb-3">
        <div className="flex items-center gap-3">
          <span className={`px-2 py-1 rounded text-xs font-medium ${statusColors[dream.status as keyof typeof statusColors]}`}>
            {dream.status === 'complete' ? 'Analyzed' : dream.status === 'new' ? 'Pending' : 'Processing...'}
          </span>
          {dream.visibility === 'private' && (
            <span className="text-dream-400 text-xs">Private</span>
          )}
        </div>
        <span className="text-dream-400 text-sm">
          {formatDistanceToNow(new Date(dream.createdAt), { addSuffix: true })}
        </span>
      </div>
      <p className="text-dream-100 line-clamp-3">{dream.rawContent}</p>
      {dream.stressCode && (
        <div className="mt-3 flex items-center gap-2">
          <span className="text-dream-400 text-sm">Stress:</span>
          <div className="flex gap-0.5">
            {[...Array(10)].map((_, i) => (
              <div
                key={i}
                className={`w-2 h-4 rounded-sm ${
                  i < dream.stressCode! ? 'bg-dream-500' : 'bg-dream-800'
                }`}
              />
            ))}
          </div>
        </div>
      )}
    </Link>
  );
}
